import {
    Box, TextField, Typography, Avatar, Skeleton, InputAdornment, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors, inkOver } from '../../CRM/kanban-board/crm-theme';
import { formatCrmListTime } from '../../CRM/kanban-board/formatters/crm-datetime';
import { formatPhone } from '@functions/format-phone';
import { resolve } from './channel-identity';
import { LastMessagePreview } from './last-message-preview';
import { MultichannelConversation } from '../services/responses/multichannel.response';

interface Props {
    conversations: MultichannelConversation[];
    loading: boolean;
    selectedId: number | null;
    search: string;
    onSearchChange: (value: string) => void;
    onSelect: (conversation: MultichannelConversation) => void;
}

function getInitials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

/// O contato pode vir sem telefone (e-mail, Instagram): a identificação cai para o que existir.
function identify(conversation: MultichannelConversation): string {
    if (conversation.contactName) return conversation.contactName;
    if (conversation.contactPhone) return formatPhone(conversation.contactPhone);
    if (conversation.contactEmail) return conversation.contactEmail;
    return `#${conversation.id}`;
}

export const MultichannelList = ({
    conversations, loading, selectedId, search, onSearchChange, onSelect,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const filtered = search
        ? conversations.filter((c) =>
            identify(c).toLowerCase().includes(search.trim().toLowerCase()))
        : conversations;

    return (
        <Box
            sx={{
                width: 300,
                flexShrink: 0,
                borderRight: `1px solid ${cc.border}`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${cc.border}` }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('crmMultichannel.buscar')}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 17, color: cc.textMuted }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                        <Box key={index} sx={{ p: 1.5 }}>
                            <Skeleton height={44} />
                        </Box>
                    ))
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                        <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                            {conversations.length === 0
                                ? t('crmMultichannel.nenhumaConversa')
                                : t('crmMultichannel.nadaEncontrado')}
                        </Typography>
                    </Box>
                ) : (
                    filtered.map((conversation) => {
                        const active = conversation.id === selectedId;
                        const name = identify(conversation);
                        const channel = resolve(conversation.channel);
                        const unread = conversation.unreadCount ?? 0;

                        return (
                            <Box
                                key={conversation.id}
                                onClick={() => onSelect(conversation)}
                                sx={{
                                    display: 'flex',
                                    gap: 1.2,
                                    p: '11px 12px',
                                    cursor: 'pointer',
                                    borderTop: `1px solid ${cc.divider}`,
                                    bgcolor: active ? cc.accentSoftBg : 'transparent',
                                    borderLeft: `3px solid ${active ? cc.accent : 'transparent'}`,
                                    pl: active ? '9px' : '12px',
                                    '&:hover': { bgcolor: active ? cc.accentSoftBg : cc.countBg },
                                }}
                            >
                                {/* O canal fica no avatar, sem custar largura ao nome; a cor é a do status. */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        flexShrink: 0,
                                        alignSelf: 'flex-start',
                                        width: 36,
                                        height: 36,
                                    }}
                                >
                                    <Tooltip
                                        title={conversation.leadStatusName
                                            || t('crmMultichannel.semLead')}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                bgcolor: conversation.leadStatusColor || cc.countBg,
                                                color: conversation.leadStatusColor
                                                    ? inkOver(conversation.leadStatusColor)
                                                    : cc.textSecondary,
                                                // Sem lead não há status: o avatar fica vazado em vez de inventar uma cor.
                                                border: conversation.leadStatusColor
                                                    ? 'none'
                                                    : `1px dashed ${cc.borderStrong}`,
                                            }}
                                        >
                                            {getInitials(name)}
                                        </Avatar>
                                    </Tooltip>
                                    {/* Monocromático: a cor do avatar já é a do status; duas cores brigam. */}
                                    <Tooltip title={channel.label}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                right: -3,
                                                bottom: -3,
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                bgcolor: cc.surfaceBg,
                                                border: `1px solid ${cc.borderStrong}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <channel.Icon
                                                sx={{ fontSize: 10, color: cc.textSecondary }}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            noWrap
                                            sx={{ flex: 1, color: cc.textPrimary }}
                                        >
                                            {name}
                                        </Typography>
                                        {conversation.lastActivityAt && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    flexShrink: 0,
                                                    fontSize: '10.5px',
                                                    fontWeight: unread > 0 ? 700 : 400,
                                                    color: unread > 0
                                                        ? cc.accent
                                                        : cc.textMuted,
                                                }}
                                            >
                                                {formatCrmListTime(conversation.lastActivityAt)}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.8,
                                            mt: 0.2,
                                        }}
                                    >
                                        <LastMessagePreview
                                            conversation={conversation}
                                            color={unread > 0 ? cc.textPrimary : cc.textSecondary}
                                            fontWeight={unread > 0 ? 500 : 400}
                                        />

                                        {unread > 0 && (
                                            <Box
                                                sx={{
                                                    flexShrink: 0,
                                                    minWidth: 18,
                                                    height: 18,
                                                    px: 0.6,
                                                    borderRadius: '9px',
                                                    bgcolor: cc.accent,
                                                    color: '#fff',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {unread > 99 ? '99+' : unread}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Etapa do funil; sem lead não há etapa e a linha some em vez de chip vazio. */}
                                    {conversation.leadColumnName && (
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mt: 0.6,
                                                px: 0.7,
                                                py: '2px',
                                                borderRadius: '4px',
                                                bgcolor: cc.countBg,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '9.5px',
                                                    fontWeight: 700,
                                                    color: cc.textSecondary,
                                                }}
                                            >
                                                {conversation.leadColumnName}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};
